export const quickSpire = {
  namespaced: true,
  
  state: {
    quickSpireData: [],
    pagination: {
      current_page: 1,
      per_page: 10,
      total: 0,
      last_page: 1,
      from: 0,
      to: 0,
      has_more_pages: false
    },
    filters: {
      promo_code_filter: '',
      customer_filter: '',
      product_code_filter: '',
      search: '',
      per_page: 10,
      page: 1
    },
    loading: false,
    availableCustomers: [],
    availablePromoCodes: [],
    availableProductCodes: [],
    showResyncConfirmModal: false,
    showResyncProgressModal: false,
    resyncSyncId: null,
    resyncStatus: {
        status: 'idle',
        progress: 0,
        message: '',
        current_step: ''
    },
    // Image caching state
    imageCache: {}, // { imageKey: { url, status, error, cachedAt } }
    visibleImages: new Set(), // Track which images are currently visible
    loadingImages: new Set() // Track which images are currently loading
  },
  
  getters: {
    quickSpireData: state => state.quickSpireData,
    pagination: state => state.pagination,
    filters: state => state.filters,
    loading: state => state.loading,
    totalItems: state => state.pagination.total,
    currentPage: state => state.pagination.current_page,
    totalPages: state => state.pagination.last_page,
    entriesPerPage: state => state.pagination.per_page,
    availableCustomers: state => state.availableCustomers,
    availablePromoCodes: state => state.availablePromoCodes,
    availableProductCodes: state => state.availableProductCodes,
    showResyncConfirmModal: state => state.showResyncConfirmModal,
    showResyncProgressModal: state => state.showResyncProgressModal,
    resyncSyncId: state => state.resyncSyncId,
    resyncStatus: state => state.resyncStatus,
    // Image cache getters
    imageCache: state => state.imageCache,
    visibleImages: state => state.visibleImages,
    loadingImages: state => state.loadingImages,
    getCachedImage: (state) => (imageKey) => state.imageCache[imageKey],
    isImageLoading: (state) => (imageKey) => state.loadingImages.has(imageKey),
    isImageVisible: (state) => (imageKey) => state.visibleImages.has(imageKey)
  },
  
  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    
    SET_QUICK_SPIRE_DATA(state, data) {
      state.quickSpireData = data
    },
    
    SET_PAGINATION(state, pagination) {
      state.pagination = pagination
    },
    
    SET_FILTERS(state, filters) {
      state.filters = { ...state.filters, ...filters }
    },
    
    RESET_FILTERS(state) {
      state.filters = {
        promo_code_filter: '',
        customer_filter: '',
        product_code_filter: '',
        search: '',
        per_page: 10,
        page: 1
      }
    },

    SET_AVAILABLE_CUSTOMERS(state, customers) {
      state.availableCustomers = customers
    },
    
    SET_AVAILABLE_PROMO_CODES(state, promoCodes) {
      state.availablePromoCodes = promoCodes
    },
    
    SET_AVAILABLE_PRODUCT_CODES(state, productCodes) {
      state.availableProductCodes = productCodes
    },
    
    SET_RESYNC_CONFIRM_MODAL(state, show) {
        state.showResyncConfirmModal = show
    },
    
    SET_RESYNC_PROGRESS_MODAL(state, show) {
        state.showResyncProgressModal = show
    },
    
    SET_RESYNC_SYNC_ID(state, syncId) {
        state.resyncSyncId = syncId
    },
    
    SET_RESYNC_STATUS(state, status) {
        state.resyncStatus = { ...state.resyncStatus, ...status }
    },
    
    // Image cache mutations
    SET_IMAGE_LOADING(state, imageKey) {
        state.loadingImages.add(imageKey)
    },
    
    SET_IMAGE_LOADED(state, { imageKey, url }) {
        state.loadingImages.delete(imageKey)
        state.imageCache[imageKey] = {
            url,
            status: 'loaded',
            error: null,
            cachedAt: Date.now()
        }
    },
    
    SET_IMAGE_ERROR(state, { imageKey, error }) {
        state.loadingImages.delete(imageKey)
        state.imageCache[imageKey] = {
            url: null,
            status: 'error',
            error,
            cachedAt: Date.now()
        }
    },
    
    SET_IMAGE_VISIBLE(state, imageKey) {
        state.visibleImages.add(imageKey)
    },
    
    SET_IMAGE_HIDDEN(state, imageKey) {
        state.visibleImages.delete(imageKey)
    },
    
    CLEAR_IMAGE_CACHE(state) {
        state.imageCache = {}
        state.visibleImages.clear()
        state.loadingImages.clear()
    },
    
    REMOVE_EXPIRED_IMAGES(state, maxAge = 3600000) { // 1 hour default
        const now = Date.now()
        Object.keys(state.imageCache).forEach(key => {
            if (now - state.imageCache[key].cachedAt > maxAge) {
                delete state.imageCache[key]
            }
        })
    }
  },
  
  actions: {
    setQuickSpireData({ commit }, { data, pagination }) {
      commit('SET_QUICK_SPIRE_DATA', data)
      commit('SET_PAGINATION', pagination)
    },
    
    setFilters({ commit }, filters) {
      commit('SET_FILTERS', filters)
    },
    
    resetFilters({ commit }) {
      commit('RESET_FILTERS')
    },
    
    setLoading({ commit }, loading) {
      commit('SET_LOADING', loading)
    },

    setAvailableCustomers({ commit }, customers) {
        commit('SET_AVAILABLE_CUSTOMERS', customers)
    },
    
    setAvailablePromoCodes({ commit }, promoCodes) {
        commit('SET_AVAILABLE_PROMO_CODES', promoCodes)
    },
    
    setAvailableProductCodes({ commit }, productCodes) {
        commit('SET_AVAILABLE_PRODUCT_CODES', productCodes)
    },
    
    setResyncConfirmModal({ commit }, show) {
        commit('SET_RESYNC_CONFIRM_MODAL', show)
    },
    
    setResyncProgressModal({ commit }, show) {
        commit('SET_RESYNC_PROGRESS_MODAL', show)
    },
    
    setResyncSyncId({ commit }, syncId) {
        commit('SET_RESYNC_SYNC_ID', syncId)
    },
    
    setResyncStatus({ commit }, status) {
        commit('SET_RESYNC_STATUS', status)
    },
    
    resetResyncState({ commit }) {
        commit('SET_RESYNC_CONFIRM_MODAL', false)
        commit('SET_RESYNC_PROGRESS_MODAL', false)
        commit('SET_RESYNC_SYNC_ID', null)
        commit('SET_RESYNC_STATUS', {
            status: 'idle',
            progress: 0,
            message: '',
            current_step: ''
        })
    },
    
    // Image cache actions
    setImageLoading({ commit }, imageKey) {
        commit('SET_IMAGE_LOADING', imageKey)
    },
    
    setImageLoaded({ commit }, { imageKey, url }) {
        commit('SET_IMAGE_LOADED', { imageKey, url })
    },
    
    setImageError({ commit }, { imageKey, error }) {
        commit('SET_IMAGE_ERROR', { imageKey, error })
    },
    
    setImageVisible({ commit }, imageKey) {
        commit('SET_IMAGE_VISIBLE', imageKey)
    },
    
    setImageHidden({ commit }, imageKey) {
        commit('SET_IMAGE_HIDDEN', imageKey)
    },
    
    clearImageCache({ commit }) {
        commit('CLEAR_IMAGE_CACHE')
    },
    
    cleanupExpiredImages({ commit }, maxAge) {
        commit('REMOVE_EXPIRED_IMAGES', maxAge)
    }
  }
}